// Cross-cutting strings, owned by no one screen: the shell, the login, the copy every
// list reuses. This is the one file a feature in any area may still need to touch.
//
// Pure data — no imports, ever. errors.js reads he.leads.needContact at module scope,
// so a part reaching back for it would hand that read a half-built object. See he.js.
const shared = {
  app: 'ניהול פיננסי',
  // Each person's light/dark choice, in the top bar (context/ThemeContext).
  theme: {
    label: 'מראה',
    system: 'לפי המכשיר',
    light: 'בהיר',
    dark: 'כהה',
  },
  nav: {
    board: 'לוח משימות',
    leads: 'לידים',
    dashboard: 'לוח בקרה',
    retainers: 'ריטיינרים',
    jobs: 'פרויקטים חד־פעמיים',
    money: 'כספים',
    // THE ONE EXPENSE SCREEN, so the label is no longer הוצאות משרד — office overhead
    // is one of its two sections, not the whole of it.
    expenses: 'הוצאות',
    // Two document screens, and the nav has to keep them apart: this one is the OFFICE
    // filing cabinet, whose own page is already titled 'מסמכי העסק', and the one after
    // it is every file that belongs to a client. A bare 'מסמכים' beside 'מסמכי לקוחות'
    // said nothing about which was which.
    documents: 'מסמכי העסק',
    clientDocuments: 'מסמכי לקוחות',
    clients: 'לקוחות',
    settlements: 'התחשבנות',
    settings: 'הגדרות',
    logout: 'התנתקות',
    menu: 'תפריט',
  },
  login: {
    email: 'דוא"ל',
    password: 'סיסמה',
    submit: 'כניסה',
    error: 'דוא"ל או סיסמה שגויים',
  },
  common: {
    add: 'הוספה',
    save: 'שמירה',
    cancel: 'ביטול',
    close: 'סגירה',
    edit: 'עריכה',
    delete: 'מחיקה',
    loading: 'טוען...',
    empty: 'אין נתונים להצגה',
    error: 'אירעה שגיאה',
    yes: 'כן',
    no: 'לא',
    total: 'סה"כ',
    of: 'מתוך',
    actions: 'פעולות',
    // The empty first option of a select that has no default and must be answered.
    // Named rather than blank: an empty row reads as a legitimate "none" choice.
    choose: 'יש לבחור…',
    // The client picker (components/ClientPicker): a box typed into, and what it says when
    // nothing matches.
    typeToSearch: 'הקלדה לחיפוש…',
    noMatches: 'לא נמצאו תוצאות',
    // הזנת תאריך: מקלידים ספרות (12052029) והשדה מסדר אותן ל-12/05/2029, או בוחרים
    // מלוח השנה. תאריך חלקי או לא קיים אינו נשמר — השמירה נחסמת עד שהוא מושלם.
    datePlaceholder: 'dd/mm/yyyy',
    openCalendar: 'בחירה מלוח שנה',
    dateIncomplete: 'תאריך חלקי — יש להזין יום, חודש ושנה (8 ספרות).',
    dateInvalid: 'התאריך אינו קיים.',
    dateFallbackLabel: 'תאריך',
    unfinishedFields: (labels) => `יש להשלים לפני שמירה: ${labels.join(', ')}`,
    // The permanent-delete dialog, shared by the job and retainer versions. A generic
    // "are you sure?" is worthless when the two records being deleted look identical on
    // screen and are nothing alike underneath, so the frame always NAMES what goes.
    deleteQuestion: 'למחוק לצמיתות?',
    deleteWhatGoes: 'יימחק יחד עם זה:',
    deleteNothingAttached: 'לא תלוי בזה שום דבר נוסף.',
    deleteIrreversible: 'המחיקה היא לצמיתות. אי אפשר לבטל אותה ואי אפשר לשחזר את המידע.',
    deletePermanently: 'מחיקה לצמיתות',
    deleting: 'מוחק...',
    // The delete is armed by typing the record's name. A click-only confirmation is
    // answered by reflex; typing the name forces it to be read, and proves which
    // record is open.
    deleteTypeToConfirm: 'לאישור המחיקה, הקלידו את השם המדויק',
    deleteTypeHint: 'יש להקליד:',
    // The item labels of that list. Shared, because the two deletes destroy the same
    // kinds of thing — one of them a level further down.
    deleteInvoices: 'חשבוניות',
    deletePayments: 'רישומי תשלום',
    deleteExpenses: 'הוצאות',
    deleteTasks: 'משימות',
    deletePublications: 'פרסומים',
    // Spelled out because it is the one thing that leaves the database entirely.
    deleteFiles: 'קבצים שהועלו — יימחקו גם מהאחסון',
    // Its own line rather than folded into the file count above: a signed contract may
    // be the only copy there is, and "12 קבצים" warns about it not at all.
    deleteAgreements: 'הסכמים חתומים — יימחקו גם מהאחסון',
    // קבצים שכותבי תוכן העלו (064). שורה נפרדת מ'קבצים', כי אלה קבצים שמישהו אחר שלח
    // ואף אחד במשרד לא תייק — אזהרה שמאחדת אותם עם השאר אינה מזהירה עליהם כלל.
    deleteWorkFiles: 'קבצים שהעלו כותבי תוכן — יימחקו גם מהאחסון',
  },
  empty: {
    jobs: 'עדיין אין פרויקטים חד־פעמיים',
    clients: 'עדיין אין לקוחות',
    inactiveClients: 'אין לקוחות לא פעילים',
    clientSearch: 'לא נמצאו לקוחות תואמים',
    invoices: 'עדיין אין חשבוניות לעבודה',
    expenses: 'עדיין אין הוצאות לעבודה',
    tasks: 'עדיין אין משימות',
    overdue: 'אין חשבוניות באיחור',
    revenueStreams: 'עדיין אין סוגי הכנסה',
    paymentMethods: 'עדיין אין אמצעי תשלום',
    paymentTerms: 'עדיין אין תנאי תשלום',
    leadSources: 'עדיין אין מקורות ליד',
    leads: 'עדיין אין לידים',
    expenseCategories: 'עדיין אין קטגוריות הוצאה',
    incomeCategories: 'עדיין אין קטגוריות הכנסה',
    // The income card on a job, empty. Its example is the brokerage case, which is the
    // reason a job carries income of its own at all.
    jobIncome: 'עדיין לא נרשמה הכנסה לעבודה',
    publications: 'עדיין אין פרסומים לעבודה',
    outlets: 'עדיין אין עיתונים ברשימה',
    jobPublications: 'אין פרסומים לעבודה זו.',
    clientJobs: 'עדיין אין עבודות ללקוח זה.',
    unpaidExpenses: 'אין הוצאות שממתינות לתשלום',
    retainers: 'עדיין אין ריטיינרים פעילים',
    doneTopics: 'אין עבודות שהסתיימו בריטיינר הזה.',
    clientDoneJobs: 'אין עבודות שהסתיימו ללקוח זה.',
    documents: 'עדיין אין מסמכים',
    documentsArchive: 'אין מסמכים בארכיון.',
    documentCategories: 'עדיין אין קטגוריות מסמכים',
    topics: 'עדיין אין עבודות לריטיינר',
    clientRetainers: 'עדיין אין ריטיינרים ללקוח זה.',
    clientProjects: 'אין ללקוח זה פרויקטים חד־פעמיים.',
    clientStandaloneJobs: 'אין ללקוח זה עבודות עצמאיות.',
    retainerTopics: 'אין עבודות תחת ריטיינר זה.',
    agreements: 'עדיין לא הועלה הסכם חתום לריטיינר זה.',
    // The same list on a JOB screen, where the owner is a job — the empty state is the
    // one place the copy has to name it, so it cannot be shared with the retainer's.
    jobAgreements: 'עדיין לא הועלה הסכם חתום לעבודה זו.',
    agreementsArchive: 'אין הסכמים בארכיון.',
  },
};

export default shared;
